import { keepPreviousData } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { RenderSuggestionParams } from "react-autosuggest";
import { Form } from "react-bootstrap";
import {
  Control,
  FieldValues,
  Path,
  UseFormGetValues,
  UseFormRegister,
} from "react-hook-form";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { SecuritiesController } from "../../../api/src/securities/securities.controller";
import { useAuthenticatedFetch } from "../lib/api";
import { useDebounceValue } from "../lib/forms";
import Autocomplete, {
  AutocompleteControlled,
  Suggestion,
} from "./Autocomplete";

type SymbolSuggestion = {
  symbol: string;
  description: string;
  currentPrice?: number | null;
  previousPrice?: number | null;
};

function SymbolField<TForm extends FieldValues>({
  fieldName,
  register,
  control,
  getValues,
  onSelect,
  description,
  className,
  disabled,
  includePrices = false,
}: {
  fieldName?: Path<TForm>;
  register?: UseFormRegister<TForm>;
  control?: Control<TForm>;
  getValues?: UseFormGetValues<TForm>;
  onSelect?: (suggestion: SymbolSuggestion) => void;
  description?: string;
  className?: string;
  disabled?: boolean;
  includePrices?: boolean;
}) {
  const [name, setName] = useState("");
  // Look up the typed text only once it has not changed for 300 ms, so one
  // typed ticker sends one lookup request rather than one for each key.
  const searchName: string = useDebounceValue(name, 300);
  const { data, refetch, isFetching } = useAuthenticatedFetch<
    SerializedObject<UnpackResponse<SecuritiesController["getTickers"]>>
  >(
    // The first page of 10: `GET /v1/securities/tickers` is paginated, and 10
    // is the number of matches it answered before it took a page, so the
    // suggestions are the same.
    `/v1/securities/tickers?name=${encodeURIComponent(searchName)}&limit=10${includePrices ? "&additional_data=prices" : ""}`,
    undefined,
    { enabled: false, placeholderData: keepPreviousData },
    ["/v1/securities/tickers", searchName, includePrices],
  );

  const [innerDescription, setInnerDescription] = useState(description ?? "");

  const loadSuggestions = useCallback(
    (value: string) => {
      // Autocomplete asks for "" when it clears the suggestions, which a blur
      // does too. Keep the typed ticker so its answer still sets the
      // description after the advisor leaves the field.
      if (value.length > 0) {
        setName(value);
      }
    },
    [setName],
  );

  const getSuggestionValue = useCallback(
    (suggestion: SymbolSuggestion) => suggestion.symbol,
    [],
  );

  const renderSuggestion = useCallback(
    (suggestion: SymbolSuggestion, params: RenderSuggestionParams) => (
      <Suggestion active={params.isHighlighted} title={suggestion.description}>
        {suggestion.symbol}
      </Suggestion>
    ),
    [],
  );

  // Set the description based on the matching security.
  useEffect(() => {
    if (data?.data) {
      const matchingSecurity = (data?.data ?? []).find(
        (value) => value.symbol.toUpperCase() === name.toUpperCase(),
      );

      if (typeof matchingSecurity !== "undefined") {
        setInnerDescription(matchingSecurity.description);
      }
    }
  }, [data, name]);

  // Update the symbol suggestions when modified.
  useEffect(() => {
    if (searchName.length > 0) {
      refetch();
    }
  }, [searchName, refetch]);

  // Initialize the inner symbol value state on load.
  useEffect(() => {
    if (
      typeof description === "undefined" &&
      typeof fieldName !== "undefined" &&
      typeof getValues !== "undefined"
    ) {
      setName(getValues(fieldName) ?? "");
    }
  }, [description, fieldName, getValues]);

  const onSelectOuter = useCallback(
    (suggestion: SymbolSuggestion) => {
      setInnerDescription(suggestion.description);
      // Match later answers against the selected symbol, so an answer for the
      // typed text does not replace the description of the selection.
      setName(suggestion.symbol);
      if (onSelect) {
        onSelect(suggestion);
      }
    },
    [onSelect],
  );

  return (
    <>
      {typeof control === "undefined" || typeof fieldName === "undefined" ? (
        <Autocomplete
          suggestions={(data?.data ?? [])
            .filter((value) =>
              value.symbol.toLowerCase().includes(name.toLowerCase()),
            )
            .map((value) => ({
              symbol: value.symbol,
              description: value.description,
              currentPrice: value.currentPrice || null,
              previousPrice: value.previousPrice || null,
            }))}
          isLoading={isFetching}
          loadSuggestions={loadSuggestions}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          name={fieldName}
          register={register}
          getValues={getValues}
          onSelect={onSelectOuter}
          className={className}
          disabled={disabled}
        />
      ) : (
        <AutocompleteControlled
          suggestions={(data?.data ?? [])
            .filter((value) =>
              value.symbol.toLowerCase().includes(name.toLowerCase()),
            )
            .map((value) => ({
              symbol: value.symbol,
              description: value.description,
              currentPrice: value.currentPrice || null,
              previousPrice: value.previousPrice || null,
            }))}
          isLoading={isFetching}
          loadSuggestions={loadSuggestions}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          name={fieldName}
          control={control}
          onSelect={onSelectOuter}
          className={className}
          disabled={disabled}
        />
      )}
      <Form.Text
        style={{
          fontSize: "0.75rem",
          display: "block",
          marginTop: "0",
          marginBottom: "0",
          lineHeight: "1.2",
        }}
      >
        {innerDescription}
      </Form.Text>
    </>
  );
}

export default SymbolField;
